import { useContext } from 'react';
import type { RouteComponentProps } from '@react-router';
import { RoutesContext } from './RoutesContext';

export interface RoutePath<T extends { [K in keyof T]?: string } = {}>
  extends Readonly<{
    path: string;
    exact?: boolean;
    component: (args: RouteComponentProps<T>) => JSX.Element | null;
  }> {}

/**
 * A top-level app route — an entry in `router.tsx` / `router.ssr.tsx`, i.e. one of the apps
 * the root router mounts. Every app must declare whether it is guest-facing (visible to a
 * host's guests) or host-only; omitting it is a compile error, which is the build gate.
 *
 * Routes nested *inside* an app keep using {@link RoutePath} — they inherit their app's
 * declaration from context. See appGuestFacing.tsx.
 */
export interface AppRoutePath<T extends { [K in keyof T]?: string } = {}>
  extends RoutePath<T>,
    Readonly<{
      isGuestFacing: boolean;
    }> {}

export const useRouterHelper = () => {
  const context = useContext(RoutesContext);
  if (context === undefined) {
    throw new Error('Routes context not defined');
  }
  return context;
};
